import React, { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { get, map } from 'lodash';
import { RootState } from 'modules';
import moment from 'moment';
import { AutoCompleteInput } from 'components/base/AutoComplete';
import WithdrawalConfirmModal from 'components/user/DeleteAccountConfirmModal';
import FullnameChangeModal from 'components/user/FullnameChangeModal';
import MenteeEdit from 'components/user/MenteeEdit';
import MentorEdit, { ScheduleEdit } from 'components/user/MentorEdit';
import NicknameChangeModal from 'components/user/NicknameChangeModal';
import PasswordChangeModal from 'components/user/PasswordChangeModal';
import ProfileImageList from 'components/user/ProfileImageList';
import SMSVerificationModal from 'components/user/SMSVerificationModal';
import TimezoneChangeModal from 'components/user/TimezoneChangeModal';
import { handleFieldError } from 'libs/api/errorHandle';
import {
  autoCompleteCompanyName,
  autoCompletePositionName,
  autoCompleteUniversityName,
  autoCompleteMajorName,
} from 'libs/api/main';
import { updateProfileImage } from 'libs/api/user';
import { showNotification, filteredFileNames } from 'libs/utils';
import { TModalState } from 'modules/types';
import { TUser, setUserInfo, TUpdateProfileImage } from 'modules/user';
import { CWindowWidth, CDefaultDateFormat, CMonthFormat } from 'constants/base.const';
import { CDegreeType } from 'constants/user.const';
import { EUserType, EDegreeType, EEmploymentType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Radio, DatePicker, Divider, Button, Checkbox, Form, Input, Typography, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { CheckSquareTwoTone } from '@ant-design/icons';
import styled from 'styled-components';
import { FormWrapper, SecondaryColor, Colors } from 'GlobalStyles';

const { Title, Text } = Typography;
const UserEditWrapper = styled.div`
  .ant-typography {
    color: #000;
  }
  .content-title {
    color: #000;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .notification-box {
    border: 1px solid ${Colors.grey0};
    padding: 20px;
    border-radius: 15px;
  }
  .profile-image {
    .user-type-tag {
      text-align: center;
      width: 40px;
      height: 20px;
      line-height: 20px;
      top: -10px;
      display: block;
      position: relative;
      margin: auto;
      font-size: 11px;
      border-radius: 4px;
      box-shadow: 0 0 0 2px #fff;
    }
  }
  .input-inline {
    .ant-form-item-label {
      font-size: 14px;
      padding: 0px;
    }
    .ant-input[disabled] {
      color: #000;
      cursor: default;
    }
    .ant-input {
      font-weight: bold;
      color: #000;
      padding: 0px;
    }
    .ant-form-item-control-input {
      min-height: 26px;
    }
  }
  .ant-form-item {
    margin-bottom: 8px;
  }
`;
const UserEdit = () => {
  const [form] = Form.useForm();
  const [passwordChangeModal, setPasswordChangeModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [fullnameChangeModal, setFullnameChangeModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [nicknameChangeModal, setNicknameChangeModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [timezoneChangeModal, setTimezoneChangeModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const [secessionConfirmModal, setSecessionConfirmModal] = useState<TModalState>({
    record: '',
    visible: false,
  });
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const userType = get(currentUser, 'user_type', EUserType.Mentee);
  const dispatch = useDispatch();
  const [smsVerificationModal, setSMSVerificationModal] = useState<TModalState>({
    record: '',
    visible: false,
  });

  useEffect(() => {
    form.setFieldsValue({
      userid: get(currentUser, 'userid', ''),
      nickname: get(currentUser, 'nickname', ''),
      phone: get(currentUser, 'phone', ''),
      fullname_local: get(currentUser, 'fullname', ''),
      timezone: get(currentUser, 'timezone', ''),
      educations: map(get(currentUser, 'educations'), (v) => ({
        ...v,
        date_from: get(v, 'date_from') && moment(get(v, 'date_from'), CDefaultDateFormat),
        date_to: get(v, 'date_to') && moment(get(v, 'date_to'), CDefaultDateFormat),
      })),
      work_experiences: map(get(currentUser, 'work_experiences'), (v, index) => ({
        ...v,
        is_primary: get(v, 'is_primary') === true ? [index] : [],
        date_from: get(v, 'date_from') && moment(get(v, 'date_from'), CDefaultDateFormat),
        date_to: get(v, 'date_to') && moment(get(v, 'date_to'), CDefaultDateFormat),
      })),
      use_nickname: get(currentUser, 'use_nickname'),
      hide_company_name: get(currentUser, 'hide_company_name'),
      hide_university_name: get(currentUser, 'hide_university_name'),
    });
  }, []);

  function handleUpdateProfileImage() {
    (async () => {
      try {
        const response = await updateProfileImage({
          profile_image_ids: filteredFileNames(form.getFieldValue('profile_images')),
        } as TUpdateProfileImage);
        //변경된 이미지 post한뒤의 response.data로 유저 리덕스 관리
        dispatch(setUserInfo(get(response, 'data') as TUser));
        showNotification('success', '프로필 사진이 변경되었습니다');
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      }
    })();
  }

  function handleCheckPrimary(checkedValue: any) {
    // 대표경력이 하나만 표시되도록
    const workExperiences = form.getFieldValue('work_experiences');
    if (workExperiences) {
      map(workExperiences, (elem, index) => {
        form.setFields([{ name: ['work_experiences', index, 'is_primary'], value: [] }]);
      });
      if (checkedValue && checkedValue.length > 0) {
        form.setFields([
          {
            name: ['work_experiences', Number(checkedValue), 'is_primary'],
            value: [Number(checkedValue)],
          },
        ]);
      }
    }
  }

  return (
    <UserEditWrapper>
      <div
        style={{
          maxWidth: `${CWindowWidth - 400}px`,
          margin: 'auto',
          padding: '40px 10px 0px 10px',
        }}
      >
        <Title level={2}>내 정보</Title>
      </div>
      <div
        style={{
          maxWidth: `${CWindowWidth - 400}px`,
          padding: '30px 10px',
          margin: 'auto',
        }}
      >
        <Row gutter={[32, 16]}>
          <Col
            xs={{ span: 24, order: 2 }}
            sm={{ span: 24, order: 2 }}
            md={{ span: 16, order: 1 }}
            lg={{ span: 16, order: 1 }}
            xl={{ span: 17, order: 1 }}
          >
            <FormWrapper style={{ padding: '0px', maxWidth: '600px', margin: '0px' }}>
              <Form form={form} name='login' layout='vertical'>
                {userType === EUserType.Mentor && <ScheduleEdit form={form} />}
                <div className='content-title'>계정</div>
                <Row align='middle' style={{ marginBottom: '20px' }} justify='space-between'>
                  <div className='profile-image'>
                    <Form.Item name='profile_images'>
                      <ProfileImageList
                        form={form}
                        user={currentUser}
                        imageId={get(currentUser, 'profile_image.id')}
                        imageUrl={get(currentUser, 'profile_image.absolute_url')}
                        handleUpdateProfileImage={handleUpdateProfileImage}
                      />
                    </Form.Item>
                  </div>
                  {/* <Button size="small" onClick={handleUpdateProfileImage}>
                              프로필 사진 변경
                            </Button> */}
                </Row>
                <Form.Item name={'userid'} label='이메일' className='form-item input-inline'>
                  <Input size='large' disabled />
                </Form.Item>
                <Input.Group>
                  <Row justify='space-between' align='middle'>
                    <Col flex='auto'>
                      <Form.Item name='password' label='비밀번호' className='form-item input-inline'>
                        <span style={{ color: '#000' }}>
                          &#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;&#8226;
                        </span>
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        onClick={() => {
                          setPasswordChangeModal({ visible: true });
                        }}
                      >
                        수정
                      </Button>
                    </Col>
                  </Row>
                </Input.Group>
                <Input.Group>
                  <Row justify='space-between' align='middle'>
                    <Col flex='auto'>
                      <Form.Item name={'fullname_local'} label='이름' className='form-item input-inline'>
                        <Input size='large' disabled />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        onClick={() => {
                          setFullnameChangeModal({
                            visible: true,
                            record: currentUser,
                            onFinish: (data: string) => {
                              form.setFields([{ name: 'fullname_local', value: data }]);
                            },
                          });
                        }}
                      >
                        수정
                      </Button>
                    </Col>
                  </Row>
                </Input.Group>
                <Input.Group>
                  <Row justify='space-between' align='middle'>
                    <Col flex='auto'>
                      <Form.Item name={'nickname'} label='닉네임' className='form-item input-inline'>
                        <Input size='large' disabled />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        onClick={() => {
                          setNicknameChangeModal({
                            visible: true,
                            record: currentUser,
                            onFinish: (data: string) => {
                              form.setFields([{ name: 'nickname', value: data }]);
                            },
                          });
                        }}
                      >
                        수정
                      </Button>
                    </Col>
                  </Row>
                </Input.Group>
                <Input.Group>
                  <Row justify='space-between' align='middle'>
                    <Col flex='auto'>
                      <Form.Item name={'phone'} label='휴대전화' className='form-item input-inline'>
                        <Input size='large' disabled />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        onClick={() => {
                          setSMSVerificationModal({
                            visible: true,
                            record: { type: 'phone' },
                            onFinish: (data: string) => {
                              form.setFields([{ name: 'phone', value: data }]);
                            },
                          });
                        }}
                      >
                        수정
                      </Button>
                    </Col>
                  </Row>
                </Input.Group>
                <Input.Group>
                  <Row justify='space-between' align='middle'>
                    <Col flex='auto'>
                      <Form.Item name={'timezone'} label='타임존' className='form-item input-inline'>
                        <Input size='large' disabled />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        onClick={() => {
                          setTimezoneChangeModal({
                            visible: true,
                            record: currentUser,
                            onFinish: (data: string) => {
                              form.setFields([{ name: 'timezone', value: data }]);
                            },
                          });
                        }}
                      >
                        수정
                      </Button>
                    </Col>
                  </Row>
                </Input.Group>
                <div style={{ marginTop: '25px' }} className='content-title'>
                  학력사항
                </div>
                <Input.Group className='form-inline'>
                  <Form.List name='educations'>
                    {(fields, { add, remove }) => (
                      <div>
                        {fields.map((field, index) => (
                          <Form.Item key={field.key} noStyle={true}>
                            <Form.Item
                              name={[field.name, 'degree_type']}
                              className='form-item form-item-no-label'
                              rules={[{ required: true }]}
                            >
                              <Radio.Group className='col-6'>
                                <Radio.Button value={EDegreeType.Bachelor}>
                                  {CDegreeType[EDegreeType.Bachelor]}
                                </Radio.Button>
                                <Radio.Button value={EDegreeType.Master}>
                                  {CDegreeType[EDegreeType.Master]}
                                </Radio.Button>
                                <Radio.Button value={EDegreeType.Doctor}>
                                  {CDegreeType[EDegreeType.Doctor]}
                                </Radio.Button>
                                <Radio.Button value={EDegreeType.MBA}>
                                  {CDegreeType[EDegreeType.MBA]}
                                </Radio.Button>
                              </Radio.Group>
                            </Form.Item>
                            <Form.Item noStyle>
                              <Row>
                                <Col flex='auto'>
                                  <Form.Item
                                    name={[field.name, 'university_name']}
                                    className='form-item'
                                    label='학교명'
                                    rules={[{ required: true }]}
                                  >
                                    <AutoCompleteInput
                                      defaultValue={get(
                                        get(currentUser, 'educations', [''])[field.name],
                                        'university_name',
                                      )}
                                      promise={autoCompleteUniversityName}
                                      form={form}
                                      name={[field.name, 'university_name']}
                                    >
                                      <Input
                                        autoComplete='off'
                                        size='large'
                                        placeholder='학교명을 입력해주세요'
                                      />
                                    </AutoCompleteInput>
                                  </Form.Item>
                                </Col>
                                {fields.length > 1 && (
                                  <Col flex='50px'>
                                    <div
                                      className='list-delete-button'
                                      onClick={() => {
                                        remove(field.name);
                                      }}
                                    >
                                      <DeleteOutlined />
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'major']}
                              className='form-item'
                              label='전공'
                              rules={[{ required: true }]}
                            >
                              <AutoCompleteInput
                                defaultValue={get(get(currentUser, 'educations', [''])[field.name], 'major')}
                                promise={autoCompleteMajorName}
                                form={form}
                                name={[field.name, 'major']}
                              >
                                <Input autoComplete='off' size='large' placeholder='전공을 입력해주세요' />
                              </AutoCompleteInput>
                            </Form.Item>
                            <Form.Item noStyle>
                              <Input.Group>
                                <Row gutter={[8, 8]}>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'date_from']}
                                      className='form-item'
                                      label='입학년도'
                                      rules={[{ required: true }]}
                                    >
                                      <DatePicker
                                        picker='month'
                                        format={CMonthFormat}
                                        placeholder='입학년도 선택'
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'date_to']}
                                      className='form-item'
                                      label='졸업년도'
                                      rules={[{ required: true }]}
                                    >
                                      <DatePicker
                                        picker='month'
                                        format={CMonthFormat}
                                        placeholder='졸업년도 혹은 예정년도'
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </Input.Group>
                            </Form.Item>
                          </Form.Item>
                        ))}
                        <Form.Item noStyle={true}>
                          <div className='list-plus-button'>
                            <Button
                              onClick={() => {
                                add({ degree_type: EDegreeType.Bachelor });
                              }}
                            >
                              <PlusOutlined />
                              학력사항 추가하기
                            </Button>
                          </div>
                        </Form.Item>
                      </div>
                    )}
                  </Form.List>
                </Input.Group>
                <div style={{ marginTop: '25px' }} className='content-title'>
                  경력사항
                </div>
                <Input.Group className='form-inline'>
                  <Form.List name='work_experiences'>
                    {(fields, { add, remove }) => (
                      <div>
                        {fields.map((field, index) => (
                          <Form.Item key={field.key} noStyle={true}>
                            <Form.Item
                              name={[field.name, 'employment_type']}
                              className='form-item form-item-no-label'
                              rules={[{ required: true }]}
                            >
                              <Radio.Group className='col-12'>
                                <Radio.Button value={EEmploymentType.PartTime}>인턴</Radio.Button>
                                <Radio.Button value={EEmploymentType.FullTime}>정규직</Radio.Button>
                              </Radio.Group>
                            </Form.Item>
                            <Form.Item noStyle>
                              <Row>
                                <Col flex='auto'>
                                  <Form.Item
                                    label='회사명'
                                    name={[field.name, 'company_name']}
                                    rules={[{ required: true }]}
                                    className='form-item'
                                  >
                                    <AutoCompleteInput
                                      defaultValue={get(
                                        get(currentUser, 'work_experiences', [''])[field.name],
                                        'company_name',
                                      )}
                                      promise={autoCompleteCompanyName}
                                      form={form}
                                      name={[field.name, 'company_name']}
                                    >
                                      <Input
                                        autoComplete='off'
                                        size='large'
                                        placeholder='회사명을 입력해주세요'
                                      />
                                    </AutoCompleteInput>
                                  </Form.Item>
                                </Col>
                                {fields.length > 1 && (
                                  <Col flex='50px'>
                                    <div
                                      className='list-delete-button'
                                      onClick={() => {
                                        remove(field.name);
                                      }}
                                    >
                                      <DeleteOutlined />
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </Form.Item>
                            <Form.Item
                              name={[field.name, 'position']}
                              className='form-item'
                              label='직급'
                              rules={[{ required: true }]}
                            >
                              <AutoCompleteInput
                                defaultValue={get(
                                  get(currentUser, 'work_experiences', [''])[field.name],
                                  'position',
                                )}
                                promise={autoCompletePositionName}
                                form={form}
                                name={[field.name, 'position']}
                              >
                                <Input autoComplete='off' size='large' placeholder='직급을 입력해주세요' />
                              </AutoCompleteInput>
                            </Form.Item>
                            <Form.Item noStyle>
                              <Input.Group>
                                <Row gutter={[8, 8]}>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'date_from']}
                                      className='form-item'
                                      label='시작일'
                                      rules={[{ required: true }]}
                                    >
                                      <DatePicker
                                        picker='month'
                                        format={CMonthFormat}
                                        placeholder='시작일 선택'
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item
                                      name={[field.name, 'date_to']}
                                      label='종료일'
                                      className='form-item'
                                    >
                                      <DatePicker
                                        picker='month'
                                        format={CMonthFormat}
                                        placeholder='종료일 선택'
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <span
                                  style={{
                                    marginTop: '-10px',
                                    float: 'right',
                                    fontSize: '12px',
                                    color: `${SecondaryColor}`,
                                  }}
                                >
                                  현직이면 비워주세요
                                </span>
                                {userType === EUserType.Mentor && (
                                  <Form.Item name={[field.name, 'is_primary']}>
                                    <Checkbox.Group onChange={handleCheckPrimary}>
                                      <Checkbox style={{ fontSize: '13px' }} value={field.name}>
                                        대표 경력
                                        <span
                                          style={{
                                            fontSize: '12px',
                                            color: '#aeaeae',
                                          }}
                                        >
                                          &nbsp;&nbsp;(대표 경력은 멘토님 소개에 근무 기간을 표시하는 대상
                                          회사 경력입니다.)
                                        </span>
                                      </Checkbox>
                                    </Checkbox.Group>
                                  </Form.Item>
                                )}
                              </Input.Group>
                            </Form.Item>
                          </Form.Item>
                        ))}
                        <Form.Item noStyle={true}>
                          <div className='list-plus-button'>
                            <Button
                              onClick={() => {
                                add({
                                  employment_type: EEmploymentType.PartTime,
                                });
                              }}
                            >
                              <PlusOutlined />
                              경력사항 추가하기
                            </Button>
                          </div>
                        </Form.Item>
                      </div>
                    )}
                  </Form.List>
                </Input.Group>
                <Input.Group style={{ marginTop: '30px' }} className='form-normal'>
                  <Divider />
                  <div className='content-title'>정보 공개 방식</div>
                  <Form.Item
                    name='use_nickname'
                    className='form-item form-item-no-label'
                    rules={[{ required: true }]}
                  >
                    <Radio.Group className='col-12'>
                      <Radio.Button value={false}>실명 사용</Radio.Button>
                      <Radio.Button value={true}>닉네임 사용</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name='hide_company_name'
                    className='form-item form-item-no-label'
                    rules={[{ required: true }]}
                  >
                    <Radio.Group className='col-12'>
                      <Radio.Button value={false}>회사명 공개</Radio.Button>
                      <Radio.Button value={true}>회사명 비공개</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    name='hide_university_name'
                    className='form-item form-item-no-label'
                    rules={[{ required: true }]}
                  >
                    <Radio.Group className='col-12'>
                      <Radio.Button value={false}>학교명 공개</Radio.Button>
                      <Radio.Button value={true}>학교명 비공개</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Input.Group>

                {userType === EUserType.Mentee ? <MenteeEdit form={form} /> : <MentorEdit form={form} />}
              </Form>
              <PasswordChangeModal modalState={passwordChangeModal} setModalState={setPasswordChangeModal} />
              <FullnameChangeModal modalState={fullnameChangeModal} setModalState={setFullnameChangeModal} />
              <NicknameChangeModal modalState={nicknameChangeModal} setModalState={setNicknameChangeModal} />
              <TimezoneChangeModal modalState={timezoneChangeModal} setModalState={setTimezoneChangeModal} />
              <WithdrawalConfirmModal
                modalState={secessionConfirmModal}
                setModalState={setSecessionConfirmModal}
              />
            </FormWrapper>

            <div>
              <div className='content-title'>회원 탈퇴하기</div>
              <div className='notification-box'>
                <CheckSquareTwoTone twoToneColor='#FBBC05' />
                <Text style={{ display: 'block' }}>
                  {' '}
                  &#183; 회원 탈퇴 시 사용자 정보는 1개월 간 보관 후, 탈퇴 시점 1개월 뒤에 삭제됩니다. (1개월
                  내 재가입 시 정보 복구 가능)
                </Text>
                <Text style={{ display: 'block' }}>
                  {' '}
                  &#183; 미사용 수강권, 미정산 내역 등 환금 가능한 정보도 1개월 뒤에 삭제되니 주의해주세요.
                </Text>
              </div>
              <Text
                style={{
                  color: `${Colors.danger}`,
                  textDecoration: 'underline',
                  textUnderlinePosition: 'under',
                  marginLeft: '10px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSecessionConfirmModal({
                    visible: true,
                    record: currentUser,
                  });
                }}
              >
                탈퇴하기
              </Text>
            </div>
          </Col>
          <Col
            xs={{ span: 24, order: 1 }}
            sm={{ span: 24, order: 1 }}
            md={{ span: 8 }}
            lg={{ span: 8 }}
            xl={{ span: 7 }}
            style={{ textAlign: 'center' }}
          >
            <div className='border-box hide-on-mobile' style={{ textAlign: 'left' }}>
              <Title level={4}>
                계정 정보 및 이력을
                <br />
                관리하세요.
              </Title>
              <Text style={{ fontSize: '13px' }}>
                {get(currentUser, 'fullname')}님의 계정정보 및 이력을 관리하여 더욱더 매력있는 프로필을
                만들어보세요.
              </Text>
            </div>
            {userType === EUserType.Mentee && (
              <Link to={ERoute.CSLabSwitchToMentor}>
                <Button block={true} style={{ margin: '20px 0' }}>
                  멘토 전환하기
                </Button>
              </Link>
            )}
          </Col>
        </Row>
      </div>
      <SMSVerificationModal modalState={smsVerificationModal} setModalState={setSMSVerificationModal} />
    </UserEditWrapper>
  );
};

export default UserEdit;
